/**
 * FormField — labeled input with a slot for a field-level error.
 *
 * Deliberately minimal: no validation logic, no formatting. The page
 * owns state; this component just renders it and forwards events.
 *
 * a11y: label is bound to input via htmlFor/id, and errors are announced
 * via an aria-live region so screen-readers pick them up when they
 * appear post-submit.
 */

import React, { forwardRef, useId } from 'react';

const FormField = forwardRef(function FormField(
  {
    label,
    type = 'text',
    name,
    value,
    onChange,
    onBlur,
    error,
    required,
    autoComplete,
    inputMode,
    placeholder,
    disabled,
    ...rest
  },
  ref,
) {
  const uid = useId();
  const inputId = `${uid}-${name || 'field'}`;
  const errorId = error ? `${inputId}-err` : undefined;
  return (
    <div className="auth-field">
      {label && (
        <label htmlFor={inputId} className="auth-field-label">
          {label}
          {required && <span className="auth-field-required" aria-hidden="true">*</span>}
        </label>
      )}
      <div className="auth-field-control">
        <input
          id={inputId}
          ref={ref}
          type={type}
          name={name}
          value={value ?? ''}
          onChange={onChange}
          onBlur={onBlur}
          className={`auth-field-input${error ? ' is-invalid' : ''}`}
          autoComplete={autoComplete}
          inputMode={inputMode}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-invalid={!!error || undefined}
          aria-describedby={errorId}
          {...rest}
        />
      </div>
      {error && (
        <p id={errorId} className="auth-field-error" aria-live="polite">
          {error}
        </p>
      )}
    </div>
  );
});

export default FormField;
